import React from 'react'
import {Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import StatusPill from 'src/components-new/primitives/StatusPill'

export interface BiologicalSampleRow {
  _id: string
  patientFirstName?: string
  patientLastName?: string
  patient?: string
  date?: string
  status?: string
  paperCardSerialNumber?: string
  samples?: {label: string; value: string}[]
  readableSample?: string
  [key: string]: any
}

interface BiologicalSampleColumnConfig {
  id: string
  label: string
}

// Same value/label pairs as the legacy page (src/pages/testsDBS/TestsDBS.tsx) —
// kept here (rather than PSP-driven) since the backend status enum is fixed and
// both stacks must show identical Italian labels.
export const STATUS_OPTIONS: {value: string; label: string}[] = [
  {value: 'PLANNING', label: 'In pianificazione'},
  {value: 'PLANNED', label: 'Pianificato'},
  {value: 'SAMPLING_OCCURRED', label: 'Effettuato'},
  {value: 'SAMPLING_NOT_OCCURRED', label: 'Non effettuato'},
  {value: 'PICKED_UP', label: 'Test ritirato'},
  {value: 'NOT_PICKED_UP', label: 'Test non ritirato'},
  {value: 'DELETED', label: 'Cancellato'},
]

const DEFAULT_COLUMNS: BiologicalSampleColumnConfig[] = [
  {id: 'patient', label: 'PAZIENTE'},
  {id: 'date', label: 'DATA'},
  {id: 'status', label: 'STATO'},
  {id: 'paperCardSerialNumber', label: 'N. SCHEDA'},
  {id: 'readableSample', label: 'CAMPIONE'},
]

// Default list ordering: by test lifecycle stage first (the order STATUS_OPTIONS is
// declared in — planning -> planned -> occurred/not occurred -> picked up/not picked up
// -> cancelled), then by surname/name. Mirrors the same status-then-surname-then-name
// convention used by PatientsListNew (src/pages-new/patients/PatientsListNew.tsx), where
// a fixed lifecycle rank is used instead of plain alphabetical status order. Statuses not
// found in STATUS_OPTIONS (unexpected/future values) sort last.
const STATUS_SORT_ORDER: Record<string, number> = STATUS_OPTIONS.reduce((acc, opt, index) => {
  acc[opt.value] = index
  return acc
}, {} as Record<string, number>)
const UNKNOWN_STATUS_RANK = STATUS_OPTIONS.length

export function sortSamplesByStatusAndName(list: BiologicalSampleRow[]): BiologicalSampleRow[] {
  return [...list].sort((a, b) => {
    const rankA = a.status ? STATUS_SORT_ORDER[a.status] ?? UNKNOWN_STATUS_RANK : UNKNOWN_STATUS_RANK
    const rankB = b.status ? STATUS_SORT_ORDER[b.status] ?? UNKNOWN_STATUS_RANK : UNKNOWN_STATUS_RANK
    if (rankA !== rankB) return rankA - rankB
    const lastNameCompare = (a.patientLastName ?? '').localeCompare(b.patientLastName ?? '', 'it')
    if (lastNameCompare !== 0) return lastNameCompare
    return (a.patientFirstName ?? '').localeCompare(b.patientFirstName ?? '', 'it')
  })
}

export function getStatusLabel(status: string | undefined): string {
  return STATUS_OPTIONS.find(opt => opt.value === status)?.label ?? '—'
}

function getStatusColor(status: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'SAMPLING_OCCURRED':
    case 'PICKED_UP':
      return 'success'
    case 'PLANNED':
      return 'info'
    case 'PLANNING':
    case 'NOT_PICKED_UP':
      return 'warning'
    case 'SAMPLING_NOT_OCCURRED':
    case 'DELETED':
      return 'error'
    default:
      return 'default'
  }
}

function renderCell(id: string) {
  return (row: BiologicalSampleRow): React.ReactNode => {
    if (id === 'patient') {
      return <Typography variant="body2">{row.patient ?? '—'}</Typography>
    }
    if (id === 'date') {
      return (
        <Typography variant="body2">
          {row.date ? moment(row.date).format('ddd DD MMM YYYY HH:mm') : '—'}
        </Typography>
      )
    }
    if (id === 'status') {
      return <StatusPill label={getStatusLabel(row.status)} color={getStatusColor(row.status)} />
    }
    if (id === 'readableSample') {
      return <Typography variant="body2">{row.readableSample ?? '—'}</Typography>
    }
    return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
  }
}

/**
 * Columns are PSP-driven (`biologicalSamplesConfiguration.columns`), falling back to a
 * sensible default set — mirrors the pattern in ExamsTab.tsx / virtualMeetingsColumns.tsx.
 * `patient` renders as the mobile card header and `status` as its top-right badge, same
 * convention as PatientsListNew / virtualMeetingsColumns.
 */
export function buildColumns(columnConfig?: BiologicalSampleColumnConfig[]): ColumnDef<BiologicalSampleRow>[] {
  const config = columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS
  return config.map(col => ({
    key: col.id,
    header: col.label,
    mobileCardHeader: col.id === 'patient',
    mobileCardBadge: col.id === 'status',
    render: renderCell(col.id),
  }))
}